import { useState, useRef, useEffect, useCallback } from "react"
import Webcam from "react-webcam"
import * as faceapi from "face-api.js"
import { createMember } from "../../services/memberService"
import { loadFaceModels, FACE_DETECT_OPTIONS, getReadyVideo } from "../../utils/faceModels"
import { processMemberPhotoFile } from "../../utils/memberPhotoCapture"
import { fr } from "../../locales/fr"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Input } from "../../components/ui/Input"
import { Label } from "../../components/ui/Label"
import { Select } from "../../components/ui/Select"

const textareaClass = `
  flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground
  placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
  focus-visible:ring-offset-2 focus-visible:ring-offset-background
`

export default function StepMember({ next, setMember }) {
  const webcamRef = useRef(null)
  const canvasRef = useRef(null)
  const fileInputRef = useRef(null)
  const detectingRef = useRef(false)
  const stableFramesRef = useRef(0)

  const [modelsLoaded, setModelsLoaded] = useState(false)
  const [modelsError, setModelsError] = useState(null)
  const [cameraReady, setCameraReady] = useState(false)
  const [cameraUnavailable, setCameraUnavailable] = useState(false)
  const [faceDetected, setFaceDetected] = useState(false)
  const [lightOk, setLightOk] = useState(true)
  const [captured, setCaptured] = useState(false)
  const [multipleFaces, setMultipleFaces] = useState(false)
  const [faceCentered, setFaceCentered] = useState(false)
  const [stableFrames, setStableFrames] = useState(0)
  const [uploadBusy, setUploadBusy] = useState(false)
  const [uploadMsg, setUploadMsg] = useState("")

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    cin: "",
    birthDate: "",
    gender: "",
    profession: "",
    address: "",
    notes: "",
    photo: "",
    faceDescriptor: null,
  })

  const m = fr.wizard.stepMember
  const fm = m.faceMessages
  const md = fr.memberDetail

  useEffect(() => {
    loadFaceModels()
      .then(() => setModelsLoaded(true))
      .catch((err) => {
        console.error("face-api models load failed", err)
        setModelsError(
          "Modèles de détection absents. Lancez « npm run setup:models » dans frontend/, puis rechargez la page."
        )
      })
  }, [])

  const drawGuide = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    canvas.width = 320
    canvas.height = 240

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = "rgba(0,0,0,0.35)"
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.globalCompositeOperation = "destination-out"
    ctx.beginPath()
    ctx.arc(canvas.width / 2, canvas.height / 2, 70, 0, Math.PI * 2)
    ctx.fill()
    ctx.globalCompositeOperation = "source-over"
    ctx.strokeStyle = "#22c55e"
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.arc(canvas.width / 2, canvas.height / 2, 70, 0, Math.PI * 2)
    ctx.stroke()
  }, [])

  const captureFromVideo = useCallback(async (video) => {
    const result = await faceapi
      .detectSingleFace(video, FACE_DETECT_OPTIONS)
      .withFaceLandmarks()
      .withFaceDescriptor()

    if (!result) return false

    const { x, y, width, height } = result.detection.box
    const size = Math.max(width, height)
    const canvas = document.createElement("canvas")
    const ctx = canvas.getContext("2d")

    canvas.width = 300
    canvas.height = 300
    ctx.drawImage(video, x, y, size, size, 0, 0, 300, 300)

    setForm((prev) => ({
      ...prev,
      photo: canvas.toDataURL("image/jpeg", 0.9),
      faceDescriptor: Array.from(result.descriptor),
    }))
    setCaptured(true)
    setUploadMsg("")
    stableFramesRef.current = 0
    return true
  }, [])

  const detectFace = useCallback(async () => {
    if (!modelsLoaded || captured || cameraUnavailable || detectingRef.current) return

    const video = getReadyVideo(webcamRef)
    if (!video) return

    detectingRef.current = true
    drawGuide()

    try {
      const detections = await faceapi.detectAllFaces(video, FACE_DETECT_OPTIONS)

      if (detections.length > 1) {
        setMultipleFaces(true)
        stableFramesRef.current = 0
        setStableFrames(0)
        return
      }

      setMultipleFaces(false)

      if (detections.length === 0) {
        setFaceDetected(false)
        stableFramesRef.current = 0
        setStableFrames(0)
        return
      }

      const detection = detections[0]
      setFaceDetected(true)

      const centerX = detection.box.x + detection.box.width / 2
      const centerY = detection.box.y + detection.box.height / 2
      const videoCenterX = video.videoWidth / 2
      const videoCenterY = video.videoHeight / 2
      const dx = Math.abs(centerX - videoCenterX)
      const dy = Math.abs(centerY - videoCenterY)
      const centered = dx < 140 && dy < 110

      setFaceCentered(centered)

      const sample = document.createElement("canvas")
      sample.width = video.videoWidth
      sample.height = video.videoHeight
      const sampleCtx = sample.getContext("2d")
      sampleCtx.drawImage(video, 0, 0)

      const pixels = sampleCtx.getImageData(0, 0, sample.width, sample.height).data
      let total = 0
      for (let i = 0; i < pixels.length; i += 4) {
        total += pixels[i]
      }
      const brightness = total / (pixels.length / 4)
      setLightOk(brightness > 35)

      if (centered && brightness > 35) {
        stableFramesRef.current += 1
        setStableFrames(stableFramesRef.current)
        if (stableFramesRef.current > 3) {
          await captureFromVideo(video)
        }
      } else {
        stableFramesRef.current = 0
        setStableFrames(0)
      }
    } catch (err) {
      console.error("face detection failed", err)
      setModelsError("Erreur de détection (WebGL). Importez une photo ou utilisez « Capturer ».")
    } finally {
      detectingRef.current = false
    }
  }, [modelsLoaded, captured, cameraUnavailable, drawGuide, captureFromVideo])

  const captureManual = async () => {
    const video = getReadyVideo(webcamRef)
    if (!video) return

    const ok = await captureFromVideo(video)
    if (!ok) {
      setFaceDetected(false)
      setModelsError(null)
      alert(
        "Visage non détecté. Rapprochez-vous, améliorez la lumière, ou importez une photo depuis le téléphone."
      )
    }
  }

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return

    setUploadBusy(true)
    setUploadMsg(m.uploadProcessing)
    try {
      const out = await processMemberPhotoFile(file)
      setForm((prev) => ({
        ...prev,
        photo: out.photo,
        faceDescriptor: out.faceDescriptor,
      }))
      setCaptured(true)
      setUploadMsg(out.faceFound ? m.uploadOkFace : m.uploadOkNoFace)
    } catch (err) {
      console.error(err)
      setUploadMsg(err?.message || "Import impossible")
    } finally {
      setUploadBusy(false)
    }
  }

  useEffect(() => {
    if (!modelsLoaded || !cameraReady || captured || cameraUnavailable) return

    const interval = setInterval(() => {
      detectFace()
    }, 700)

    return () => clearInterval(interval)
  }, [modelsLoaded, cameraReady, captured, cameraUnavailable, detectFace])

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }))
  }

  const save = async () => {
    const mem = await createMember(form)
    setMember(mem)
    next(2)
  }

  const videoConstraints = {
    width: 640,
    height: 480,
    facingMode: "user",
  }

  const faceHint = cameraUnavailable
    ? m.cameraUnavailable
    : modelsError
      ? modelsError
      : !modelsLoaded
        ? "Chargement des modèles de détection…"
        : !cameraReady
          ? "Activation de la caméra…"
          : multipleFaces
            ? fm.multiple
            : !faceDetected
              ? fm.none
              : !faceCentered
                ? fm.center
                : !lightOk
                  ? fm.light
                  : !captured
                    ? fm.hold
                    : fm.done

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
      <Card>
        <CardHeader>
          <CardTitle>{m.formTitle}</CardTitle>
          <CardDescription>{md.infoDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="wiz-firstName">{m.firstName}</Label>
              <Input
                id="wiz-firstName"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                autoComplete="given-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wiz-lastName">{m.lastName}</Label>
              <Input
                id="wiz-lastName"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                autoComplete="family-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wiz-phone">{m.phone}</Label>
              <Input
                id="wiz-phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                autoComplete="tel"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wiz-email">{m.emailPlaceholder}</Label>
              <Input
                id="wiz-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wiz-cin">{m.cin}</Label>
              <Input id="wiz-cin" name="cin" value={form.cin} onChange={handleChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wiz-birthDate">{md.birthDate}</Label>
              <Input id="wiz-birthDate" name="birthDate" type="date" value={form.birthDate} onChange={handleChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="wiz-gender">{fr.gender.label}</Label>
              <Select id="wiz-gender" name="gender" value={form.gender} onChange={handleChange}>
                <option value="">{fr.gender.label}</option>
                <option value="male">{fr.gender.male}</option>
                <option value="female">{fr.gender.female}</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="wiz-profession">{m.profession}</Label>
              <Input id="wiz-profession" name="profession" value={form.profession} onChange={handleChange} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="wiz-address">{m.address}</Label>
            <textarea
              id="wiz-address"
              name="address"
              value={form.address}
              onChange={handleChange}
              rows={3}
              placeholder={m.address}
              className={textareaClass}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="wiz-notes">{m.notes}</Label>
            <textarea
              id="wiz-notes"
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={2}
              placeholder={m.notes}
              className={textareaClass}
            />
          </div>
          <p className="text-xs text-muted-foreground">{m.photoOptional}</p>
          <Button type="button" className="mt-2" onClick={save}>
            {m.createMember}
          </Button>
        </CardContent>
      </Card>

      <Card className="text-center">
        <CardHeader>
          <CardTitle>{m.captureTitle}</CardTitle>
          <CardDescription>{m.captureHint}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onPhotoFile}
          />
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              type="button"
              variant="default"
              size="sm"
              disabled={uploadBusy}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadBusy ? m.uploadProcessing : m.uploadPhoto}
            </Button>
            {!cameraUnavailable && modelsLoaded && cameraReady && !captured ? (
              <Button type="button" variant="secondary" size="sm" onClick={captureManual}>
                Capturer maintenant
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">{m.uploadHint}</p>

          {!cameraUnavailable ? (
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl border border-border bg-black/50">
              <Webcam
                ref={webcamRef}
                audio={false}
                mirrored
                screenshotFormat="image/jpeg"
                videoConstraints={videoConstraints}
                onUserMedia={() => {
                  setCameraReady(true)
                  setCameraUnavailable(false)
                }}
                onUserMediaError={(err) => {
                  console.error("webcam error", err)
                  setCameraUnavailable(true)
                  setCameraReady(false)
                }}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />
            </div>
          ) : (
            <div className="flex aspect-[4/3] w-full items-center justify-center rounded-xl border border-dashed border-border bg-muted/40 px-4">
              <p className="text-sm text-muted-foreground">{m.cameraUnavailable}</p>
            </div>
          )}

          <p className="text-sm text-muted-foreground">{uploadMsg || faceHint}</p>

          {form.photo ? (
            <div className="space-y-3">
              <img
                src={form.photo}
                alt={m.faceAlt}
                className="mx-auto h-28 w-28 rounded-full border border-border object-cover"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setCaptured(false)
                  setForm((prev) => ({ ...prev, photo: "", faceDescriptor: null }))
                  stableFramesRef.current = 0
                  setStableFrames(0)
                  setUploadMsg("")
                  setModelsError(null)
                }}
              >
                {m.retake}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  )
}
